import { getBreakFromRegion, useRegion } from "@/hooks/useRegions";
import {
  answerConfigForPath,
  buildFaqs,
  factsFromPublishedForecast,
  factsFromReadings,
  formatConditionsSentence,
  rateAnswer,
  type AnswerFacts,
  type AnswerPageConfig,
  type FaqEntry,
} from "@/lib/seoAnswer";
import { publicSpotId, type PublicForecastResponse } from "@/lib/publicForecast";
import { useQuery } from "@tanstack/react-query";
import { selectDisplayedSwell } from "@/lib/displayedSwell";
import { useMarineData } from "@/lib/marine";
import { tideFactor } from "@/lib/nearshore";
import { useTideData } from "@/lib/tides";
import { useOpenMeteoWeather } from "@/lib/weather";
import { tideAtTime } from "@/components/SurfDashboard/ForecastDetailPanel";
import { useMemo } from "react";

export interface SpotAnswer {
  config: AnswerPageConfig | null;
  facts: AnswerFacts | null;
  sentence: string | null;
  faqs: FaqEntry[] | null;
}

/**
 * Conditions sentence for an SEO spot or hub.
 *
 * The sentence prefers the precomputed `/api/forecast.json` snapshot so it
 * moves with the cron instead of staying at the last HTML build. Marine
 * and wind are the fallback when that document has no swell for the spot.
 */
export function useSpotAnswer(pathname: string | null): SpotAnswer {
  const published = useQuery({
    queryKey: ["published-forecast"],
    queryFn: async (): Promise<PublicForecastResponse> => {
      const response = await fetch("/api/forecast.json");
      if (!response.ok) throw new Error(`forecast ${response.status}`);
      return response.json();
    },
    staleTime: 60_000,
    retry: 1,
  });
  const config = useMemo(
    () => (pathname ? answerConfigForPath(pathname) : null),
    [pathname],
  );
  const regionId = config?.regionId ?? "";
  const { data: region } = useRegion(regionId);
  const surfBreak =
    region && config ? getBreakFromRegion(region, config.breakId) : undefined;
  const lat = surfBreak?.lat ?? null;
  const lon = surfBreak?.lon ?? null;
  const breakType = surfBreak?.breakType ?? "beach";
  const marine = useMarineData(lat, lon, breakType, {
    regionId: config?.regionId,
  });
  const weather = useOpenMeteoWeather(lat, lon);
  const tides = useTideData(lat, lon, config?.regionId);

  const publishedFacts = useMemo(() => {
    if (!config || !region || !published.data) return null;
    const spot = published.data.spots.find(
      (item) => item.id === publicSpotId(config.regionId, config.breakId),
    );
    if (!spot) return null;
    return factsFromPublishedForecast({
      swellFt: spot.forecast.swell_ft,
      swellPeriodS: spot.forecast.swell_period_s,
      swellDirection: spot.forecast.swell_direction,
      windMph: spot.forecast.wind_mph,
      windDirection: spot.forecast.wind_direction,
      generatedAt: published.data.generated_at,
      timeZone: region.timezone,
    });
  }, [config, published.data, region]);

  const liveFacts = useMemo(() => {
    const reading = marine.data;
    const wind = weather.data;
    if (!config || !reading || !wind || !region) return null;
    const currentTide = tideAtTime(tides.data, new Date().toISOString());
    const tideQualityFactor =
      surfBreak?.calibration && currentTide
        ? tideFactor(currentTide.height, currentTide.trend, surfBreak.calibration)
        : null;
    const displayedSwell = selectDisplayedSwell({
      swellHeightM: reading.swellHeightM,
      swellPeriodS: reading.swellPeriod,
      swellDirectionDeg: reading.swellDirection,
      waveHeightM: reading.offshoreHeightM,
      wavePeriodS: reading.wavePeriod,
      waveDirectionDeg: reading.waveDirection,
    });
    const rating = rateAnswer({
      faceHeightM: reading.waveHeightM,
      periodS: reading.swellPeriod ?? reading.wavePeriod,
      windSpeedKmh: wind.currentWindSpeed,
      windGustKmh: wind.currentWindGusts,
      windDirectionDeg: wind.currentWindDirection,
      swellDirectionDeg: displayedSwell.directionDeg,
      shoreNormal: surfBreak?.shoreNormal ?? null,
      tideQualityFactor,
    });
    return factsFromReadings({
      rating,
      faceHeightM: reading.waveHeightM,
      swellHeightM: displayedSwell.heightM,
      swellPeriodS: displayedSwell.periodS,
      swellDirectionDeg: displayedSwell.directionDeg,
      windSpeedKmh: wind.currentWindSpeed,
      windDirectionDeg: wind.currentWindDirection,
      updatedAtIso: new Date(marine.dataUpdatedAt).toISOString(),
      timeZone: region.timezone,
    });
  }, [
    config,
    marine.data,
    marine.dataUpdatedAt,
    region,
    surfBreak,
    tides.data,
    weather.data,
  ]);

  const facts = publishedFacts ?? liveFacts;

  return useMemo(() => {
    if (!config) return { config: null, facts: null, sentence: null, faqs: null };
    return {
      config,
      facts,
      sentence: formatConditionsSentence(config, facts),
      faqs: buildFaqs(config, facts),
    };
  }, [config, facts]);
}
